import { KmsTypePublishCategory } from "@kaltura/mediaspace-shared-types";
import { Tooltip, Typography, TypographyProps } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import { useContext, useId } from "react";
import { CategoryBubble, CategoryBubbleType } from "../category-bubbles/CategoryBubble";
// noinspection ES6PreferShortImport
import { CategoryLinkAnalyticsContext } from "../CategoryLinkAnalyticsContext";

export interface CategoryLink extends KmsTypePublishCategory {
    url?: string;
    label?: string;
}

export type CategoriesListVariant = "regular" | "dense";

export interface CategoriesListProps {
    categories: CategoryLink[];
    className?: string;
    variant: CategoriesListVariant;
    title?: { label: string; color: TypographyProps["color"] };
    onDelete?: (category: CategoryBubbleType) => void;
}

/**
 * CategoriesList Component
 * This component displays a list of categories, each with a tooltip and an optional title (pending in:/rejected in:).
 * - If a category has a URL or if the component includes an onDelete callback, it will be rendered as a bubble.
 * - In all other cases, categories will be rendered as simple, comma-separated names.
 */
export function CategoriesList({ className = "", variant, categories, title, onDelete }: CategoriesListProps) {
    const { sendCategoryLinkAnalytics } = useContext(CategoryLinkAnalyticsContext);
    const titleId = useId();
    return (
        <>
            {!!categories.length && (
                <StyledCategories className={className}>
                    {title && (
                        <StyledTitle
                            id={titleId}
                            variant="body2Highlight"
                            color={title.color}
                            onNewLine={variant !== "dense"}
                        >
                            {title.label}
                            {variant === "dense" && ":"}
                        </StyledTitle>
                    )}
                    <StyledGroup role="group" aria-labelledby={title ? titleId : undefined}>
                        {categories.map((category, index) => {
                            const isBubble = category.url || onDelete;
                            return isBubble ? (
                                <CategoryBubble
                                    key={index}
                                    category={category}
                                    onClick={category.url ? sendCategoryLinkAnalytics : undefined}
                                    onDelete={onDelete}
                                />
                            ) : (
                                <Tooltip title={category.label} key={category.id}>
                                    <StyledCategory variant="body2Highlight">
                                        {`${category.name}${index < categories.length - 1 ? "," : ""}`}
                                    </StyledCategory>
                                </Tooltip>
                            );
                        })}
                    </StyledGroup>
                </StyledCategories>
            )}
        </>
    );
}

const StyledCategories = styled("div")(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    rowGap: theme.spacing(1),
    alignItems: "center",
}));

const StyledGroup = styled("div")({
    display: "contents",
});

const StyledCategory = styled(Typography)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));

const StyledTitle = styled(
    Typography,
    {
        shouldForwardProp: (propName) => propName !== "onNewLine",
    }
)<{ onNewLine?: boolean }>(({ theme, onNewLine }) => [
    !onNewLine && {
        marginRight: theme.spacing(0.5),
        textWrap: "nowrap",
    },
    onNewLine && {
        // force the title to be on a separate line by making it take the whole container's width
        width: "100%",
    },
]);
